import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  IconButton,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import OptionsPopup, {
  OptionPopupObjectProps,
} from "../../common/OptionsPopup";
import CustomTextField from "../../common/CustomTextField";
import Button from "../../common/Button";
import PageBackButton from "../../common/PageBackButton";
import {
  Cancel,
  CheckCircleOutline,
  HistoryOutlined,
  Inventory2Outlined,
  PaymentsOutlined,
  ReceiptLongOutlined,
} from "@mui/icons-material";
import CustomCheckbox from "../../common/CustomCheckBox";
import { SingleAlertInfo, setSingleAlertObj, showHideLoadingDialog } from "../../../redux/slice/commonSlice";
import CustomCurrencyField from "../../common/CustomCurrencyField";
import { priceFormatter } from "../../../utils/Utility";
import {
  addCreditMemo,
  getCreditMemoDetailsById,
  getInvoiceById,
  InvoiceDetailDTO,
  useInvoiceSlice,
} from "../../../redux/slice/invoice/InvoiceSlice";
import { RESPONSE_CODES } from "../../../constants/applicationConstants";
import { SUCCESS_CODE } from "../../../service/ServiceConstants";
import { useNavigation } from "../../../hooks/useNavigation";
import { fetchPracticeByIdData } from "../../../redux/slice/practice/practiceEditSlice";
import { PracticeInfo } from "../../../redux/slice/practiceSlice";
import { SectionHeader } from "../../ui/ContentSection";
import { useModalStyles } from "../../common/useModalStyles";

interface recordCreditMemoForm {
  mfgInvoice: string;
  invId: string;
  creditAmt: number;
  returnAmt: any;
  comments: number;
  productReturnId: number | null;
}

export type RecordCreditMemoProps = {
  invoiceIdOverride?: number;
  onRequestClose?: () => void;
  onSaved?: () => void;
  compact?: boolean;
  initialInvNumber?: string;
  initialInvDate?: string;
  initialPractice?: string;
  initialMfr?: string;
  initialAgency?: string;
};

const RecordCreditMemo = ({
  invoiceIdOverride,
  onRequestClose,
  onSaved,
  compact,
  initialInvNumber,
  initialInvDate,
  initialPractice,
  initialMfr,
  initialAgency,
}: RecordCreditMemoProps = {}) => {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<recordCreditMemoForm>({});

  const { navigate } = useNavigation();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("md"));
  const {
    tokens: mTokens,
    isDark: isDarkModal,
    inputFieldSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();
  const cardSx = {
    borderRadius: "16px",
    border: `1px solid ${mTokens.border}`,
    backgroundColor: mTokens.bg,
    backgroundImage: "none",
    overflow: "hidden",
    boxShadow: isDarkModal ? "0 10px 20px rgba(0,0,0,0.1)" : "none",
  };
  const dispatch = useAppDispatch();
  const [checkedList, setCheckedList] = useState<number[]>([]);
  const [showOptionPopup, setShowOptionsPopup] = useState<OptionPopupObjectProps>({ message: "", show: false });
  const [submitAction, setSubmitAction] = useState<string>();
  const [totalExtendedPrice, setTotalExtendedPrice] = useState(0);
  const [isAnyChecked, setIsAnyChecked] = useState(false);

  const { state } = useLocation();
  const invoiceId = invoiceIdOverride ?? state?.invoiceId;
  const invNumber = initialInvNumber ?? state?.invNumber;
  const invDate = initialInvDate ?? state?.invDate;
  const practiceName = initialPractice ?? state?.practice;
  const mfrName = initialMfr ?? state?.mfr;
  const agencyNameFallback = initialAgency ?? state?.agency;
  const closeOrGoBack = () => (onRequestClose ? onRequestClose() : navigate(-1));
  const { recordCreditMemo } = useInvoiceSlice();
  const [creditMemoForProduct, setCreditMemoForProduct] = useState<boolean>(false);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceDetailDTO>();
  const [practice, setPractice] = useState<PracticeInfo>();

  useEffect(() => {
    if (invoiceId) {
      setInvoiceDetails(invoiceId);
    }
  }, []);

  const setInvoiceDetails = async (invoiceId: number) => {
    dispatch(showHideLoadingDialog(true));
    try {
      await dispatch(getCreditMemoDetailsById(invoiceId));
      const response = await dispatch(getInvoiceById({ invoiceId: invoiceId, isSaveAuditLog: false }));
      if (response?.payload?.code === SUCCESS_CODE && response?.payload?.data) {
        const practiceResponse = await dispatch(fetchPracticeByIdData(response.payload.data.practiceId));
        if (practiceResponse?.payload?.code === SUCCESS_CODE && practiceResponse?.payload?.data) {
          setPractice(practiceResponse.payload.data);
        }
        setSelectedInvoice(response.payload.data);
      }
    } finally {
      dispatch(showHideLoadingDialog(false));
    }
  };

  const selectedOrderItems: { productReturnId: number } = useMemo(() => {
    const newSelectedOrderItems: { productReturnId: number }[] = checkedList?.map((id) => ({
      productReturnId: id,
    }));
    return newSelectedOrderItems[0];
  }, [checkedList]);

  const onSubmit = async (data: recordCreditMemoForm) => {
    if (submitAction === "recordedCreditAgainstReturnProduct") {
      setShowOptionsPopup({
        message:
          data.creditAmt > data.returnAmt
            ? "Credit Amount is greater than Return Amount. Do you still want to record the credit memo?"
            : data.returnAmt === data.creditAmt
              ? "Do you really want to record the credit memo?"
              : "Return Amount and Credit Amount are not equal. Do you still want to record the credit memo?",
        show: true,
        variant: "confirm",
        buttons: [
          {
            name: "Confirm",
            color: "primary",
            onClick: () =>
              submitMarkInvoiced({
                ...data,
                mfgInvoice: invNumber,
                invId: invoiceId,
                productReturnId: selectedOrderItems?.productReturnId ? selectedOrderItems?.productReturnId : null,
              }),
          },
          { name: "Cancel", color: "secondary", onClick: cancelOptionPopup },
        ],
      });
    } else if (submitAction === "recordedCredit") {
      setShowOptionsPopup({
        message: data.creditAmt ? "Do you really want to record the credit memo?" : "",
        show: true,
        variant: "confirm",
        buttons: [
          {
            name: "Confirm",
            color: "primary",
            onClick: () =>
              submitMarkInvoiced({
                ...data,
                mfgInvoice: invNumber,
                invId: invoiceId,
                productReturnId: null,
              }),
          },
          { name: "Cancel", color: "secondary", onClick: cancelOptionPopup },
        ],
      });
    }
  };

  const submitMarkInvoiced = async (data: recordCreditMemoForm) => {
    if (data?.invId && data?.mfgInvoice) {
      const creditMemoData = {
        invoiceId: data?.invId,
        mfgInvoiceNumber: data?.mfgInvoice,
        creditAmount: data?.creditAmt,
        productReturnId: data?.productReturnId,
        comments: data?.comments,
      };
      if (creditMemoData) {
        const response = await dispatch(addCreditMemo(creditMemoData));
        if (response?.payload?.code === RESPONSE_CODES.SUCCESS_CODE) {
          let alertMessage = "Credit Memo recorded successfully";
          const alertObj: SingleAlertInfo = {
            message: alertMessage,
            alertType: "success",
          };
          dispatch(setSingleAlertObj(alertObj));
          onSaved?.();
          closeOrGoBack();
        } else if (response?.payload?.code === RESPONSE_CODES.FAILED_CODE) {
          let alertMessage = response.payload?.message;
          const alertObj: SingleAlertInfo = {
            message: alertMessage,
            alertType: "error",
          };
          dispatch(setSingleAlertObj(alertObj));
        }
        cancelOptionPopup();
      }
    }
  };

  const cancelOptionPopup = () => {
    setShowOptionsPopup({ message: "", show: false });
  };

  const handleCheckboxChange = (e: any, productReturnId: number, totalReturnAmount: number) => {
    const { checked } = e.target;
    setTotalExtendedPrice(() => {
      const newVal = checked ? totalReturnAmount : 0;
      setValue("returnAmt", newVal);
      return newVal;
    });

    setCheckedList((prev) => {
      let updatedList: number[];

      if (checked) {
        updatedList = [...prev, productReturnId];
        setIsAnyChecked(true);
      } else {
        updatedList = prev.filter((id) => id !== productReturnId);
        setIsAnyChecked(updatedList.length > 0);
      }

      return updatedList;
    });
  };

  const disableCheckBox = (row: any) => {
    if (row?.productReturnId !== null) {
      return false;
    }
    return true;
  };

  const handleOrderDetailsNavigate = (data: any) => {
    const orderData = {
      orderId: data?.orderId,
      orderNumber: data?.orderNumber,
      productName: data?.productName,
      practiceName: data?.practiceName,
      po: data?.poNumber,
      dateOfService: data?.dateOfService,
      shippingCarrier: data?.shippingCarrier,
      trackingNumber: data?.trackingNumber,
      orderStatus: data?.orderStatus,
      provider: data?.providerName,
      patientName: data?.patientName,
      manufacturerName: data?.mfgName,
    };
    navigate("/details/1", { state: { orderDetails: orderData } });
  };

  const FieldLabel = ({ label, required = false }: { label: string; required?: boolean }) => (
    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: mTokens.textSecondary, mb: 0.9, display: "flex", alignItems: "center", gap: 0.4 }}>
      {label}
      {required && (
        <Box component="span" sx={{ color: mTokens.accent }}>
          *
        </Box>
      )}
    </Typography>
  );

  const InfoRow = ({ label, value, highlight = false }: { label: string; value?: React.ReactNode; highlight?: boolean }) => (
    <Box display="flex" alignItems="center" py={1.15} sx={{ borderBottom: `1px solid ${mTokens.border}`, "&:last-child": { borderBottom: "none" } }}>
      <Typography sx={{ fontSize: "0.82rem", color: mTokens.textSecondary, width: "45%", flexShrink: 0, fontWeight: highlight ? 700 : 400 }}>
        {label}
      </Typography>
      <Box
        sx={{
          fontSize: highlight ? "1rem" : "0.88rem",
          color: highlight ? "#10B981" : mTokens.textPrimary,
          fontWeight: highlight ? 800 : 600,
          flex: 1,
          minWidth: 0,
        }}
      >
        {value ?? "—"}
      </Box>
    </Box>
  );

  const colHeaderSx = {
    fontSize: "0.68rem",
    fontWeight: 700,
    color: mTokens.textSecondary,
    textTransform: "uppercase" as const,
    letterSpacing: "0.08em",
  };
  const colBodySx = { fontSize: "0.88rem", color: mTokens.textPrimary, fontWeight: 500 };
  const historyGridCols = "minmax(110px, 1fr) minmax(100px, 0.9fr) minmax(100px, 1fr) minmax(120px, 1.4fr)";
  const returnGridCols = "40px minmax(100px, 1.2fr) minmax(90px, 1fr) minmax(80px, 0.8fr) minmax(90px, 0.9fr) minmax(100px, 1.1fr) minmax(80px, 0.8fr)";

  const totalAmount = selectedInvoice?.invoiceAmount
    ? selectedInvoice.invoiceAmount + (selectedInvoice.shippingCharges ? selectedInvoice.shippingCharges : 0)
    : 0;

  return (
    <Box
      sx={
        compact
          ? {}
          : {
              mx: { xs: "10px", sm: "20px" },
              mt: "12px",
              pb: "72px",
            }
      }
    >
      {showOptionPopup.show && (
        <OptionsPopup
          open={showOptionPopup.show}
          onClose={() => setShowOptionsPopup({ message: "", show: false })}
          variant={showOptionPopup.variant}
          message={<>{showOptionPopup.message}</>}
          buttons={showOptionPopup.buttons}
          title={showOptionPopup.title}
          obj={showOptionPopup.obj}
        />
      )}

      {!compact && (
        <Box mb={2} display="flex" alignItems="flex-start" gap={1.5}>
          <PageBackButton tooltip={null} />
          <Box>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: mTokens.textSecondary, textTransform: "uppercase", letterSpacing: "0.12em", mb: 0.3 }}>
              Billing / Record Credit Memo
            </Typography>
            <Typography component="h1" sx={{ fontSize: "1.6rem", fontWeight: 800, color: mTokens.textPrimary, lineHeight: 1.1 }}>
              Record Credit Memo
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", color: mTokens.textSecondary, mt: 0.4 }}>
              Please select items to Record Credit Memo.
            </Typography>
          </Box>
        </Box>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.4fr 1fr" }, gap: 2, alignItems: "flex-start" }}>
          {/* Left: form actions */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, order: { xs: 2, md: 1 } }}>
            {recordCreditMemo?.creditMemoDTOList?.length > 0 && (
              <Box sx={cardSx}>
                <SectionHeader icon={<HistoryOutlined fontSize="small" />} title="Credit Memo History" />
                <Box>
                  {!isNarrow && (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: historyGridCols,
                        gap: 1,
                        px: 2.8,
                        py: 0.8,
                        borderBottom: `1px solid ${mTokens.border}`,
                        backgroundColor: mTokens.headerFooterBg,
                      }}
                    >
                      <Typography sx={colHeaderSx}>Date Of Credit</Typography>
                      <Typography sx={colHeaderSx}>Credit Amount</Typography>
                      <Typography sx={colHeaderSx}>User</Typography>
                      <Typography sx={colHeaderSx}>Comments</Typography>
                    </Box>
                  )}
                  {recordCreditMemo?.creditMemoDTOList?.map((row: any, index: number) => {
                    const rowBorder =
                      index < recordCreditMemo.creditMemoDTOList.length - 1 ? `1px dashed ${mTokens.border}` : "none";
                    return (
                      <Box key={row?.productReturnId ?? index}>
                        {isNarrow ? (
                          <Box sx={{ px: 2, py: 1.2, borderBottom: rowBorder }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.4 }}>
                              <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: mTokens.textPrimary }}>
                                {row.dateOfCredit}
                              </Typography>
                              <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: mTokens.accent }}>
                                {priceFormatter(row.creditAmount)}
                              </Typography>
                            </Box>
                            <Typography sx={{ fontSize: "0.78rem", color: mTokens.textSecondary }}>
                              {row.auditUserName}
                            </Typography>
                            {row.comments ? (
                              <Typography sx={{ fontSize: "0.82rem", color: mTokens.textPrimary, mt: 0.4 }}>
                                {row.comments}
                              </Typography>
                            ) : null}
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: historyGridCols,
                              gap: 1,
                              px: 2.8,
                              py: 1.2,
                              borderBottom: rowBorder,
                              alignItems: "center",
                            }}
                          >
                            <Typography sx={colBodySx}>{row.dateOfCredit}</Typography>
                            <Typography sx={{ ...colBodySx, fontWeight: 700, color: mTokens.accent }}>
                              {priceFormatter(row.creditAmount)}
                            </Typography>
                            <Typography sx={colBodySx}>{row.auditUserName}</Typography>
                            <Typography sx={{ ...colBodySx, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {row.comments}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            <Box sx={cardSx}>
              <SectionHeader icon={<PaymentsOutlined fontSize="small" />} title="Credit Memo Details" />
              <Box sx={{ px: 3.2, pt: 2.2, pb: 3.2 }}>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  mb={2.5}
                  sx={{
                    px: 1.5,
                    py: 1.2,
                    borderRadius: "12px",
                    border: `1px solid ${mTokens.border}`,
                    backgroundColor: mTokens.headerFooterBg,
                  }}
                >
                  <CustomCheckbox
                    onChange={(e) => {
                      const { checked } = e.target;
                      setCreditMemoForProduct(!creditMemoForProduct);
                      if (checked) {
                        setSubmitAction("RerturnProductSelected");
                      } else {
                        setCheckedList([]);
                        setIsAnyChecked(false);
                        setValue("returnAmt", null);
                      }
                    }}
                    control={control}
                    name={"creditMemoForProduct"}
                    checked={creditMemoForProduct}
                  />
                  <Typography sx={{ fontSize: "0.88rem", color: mTokens.textPrimary, fontWeight: 600 }}>
                    Is credit memo related to product return?
                  </Typography>
                </Box>

                {creditMemoForProduct && (
                  <Box sx={{ mb: 2.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.8,
                        mb: 1.2,
                      }}
                    >
                      <Inventory2Outlined sx={{ fontSize: "1rem", color: mTokens.accent }} />
                      <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: mTokens.textPrimary }}>
                        Returned Products
                      </Typography>
                    </Box>
                    {recordCreditMemo?.productReturnDTOList?.length > 0 ? (
                      <Box sx={{ border: `1px solid ${mTokens.border}`, borderRadius: "12px", overflow: "hidden", mb: 2 }}>
                        {!isNarrow && (
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: returnGridCols,
                              gap: 1,
                              px: 1.5,
                              py: 0.8,
                              borderBottom: `1px solid ${mTokens.border}`,
                              backgroundColor: mTokens.headerFooterBg,
                            }}
                          >
                            <Box />
                            <Typography sx={colHeaderSx}>Product Name</Typography>
                            <Typography sx={colHeaderSx}>Serial #</Typography>
                            <Typography sx={colHeaderSx}>Credit Issued?</Typography>
                            <Typography sx={colHeaderSx}>Return Date</Typography>
                            <Typography sx={colHeaderSx}>Return Reason</Typography>
                            <Typography sx={{ ...colHeaderSx, textAlign: "right" }}>Amount</Typography>
                          </Box>
                        )}
                        {recordCreditMemo?.productReturnDTOList?.map((row: any, index: number) => {
                          const rowBorder =
                            index < recordCreditMemo.productReturnDTOList.length - 1
                              ? `1px dashed ${mTokens.border}`
                              : "none";
                          return (
                            <Box key={row?.productReturnId}>
                              {isNarrow ? (
                                <Box sx={{ px: 1.5, py: 1.2, borderBottom: rowBorder }}>
                                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                    <Box sx={{ pt: 0.2 }}>
                                      <CustomCheckbox
                                        onChange={(e) => handleCheckboxChange(e, row.productReturnId, row.totalReturnAmount)}
                                        control={control}
                                        name={`select-${row.productReturnId}`}
                                        checked={checkedList.includes(row.productReturnId)}
                                        isDisabled={(disableCheckBox(row) || isAnyChecked) && !checkedList.includes(row.productReturnId)}
                                      />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.4 }}>
                                        <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: mTokens.textPrimary }}>
                                          {row.productName}
                                        </Typography>
                                        <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: mTokens.accent, flexShrink: 0 }}>
                                          {priceFormatter(row.totalReturnAmount)}
                                        </Typography>
                                      </Box>
                                      <Typography sx={{ fontSize: "0.78rem", color: mTokens.textSecondary }}>
                                        SN: {row.returnDetail || "—"}
                                      </Typography>
                                      <Typography sx={{ fontSize: "0.78rem", color: mTokens.textSecondary, mt: 0.3 }}>
                                        Credit Issued: {row.isCreditMemoRecorded ? "Yes" : "No"} · {row.returnDate}
                                      </Typography>
                                      {row.returnReason ? (
                                        <Typography sx={{ fontSize: "0.8rem", color: mTokens.textPrimary, mt: 0.3 }}>
                                          {row.returnReason}
                                        </Typography>
                                      ) : null}
                                    </Box>
                                  </Box>
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: returnGridCols,
                                    gap: 1,
                                    px: 1.5,
                                    py: 1.1,
                                    borderBottom: rowBorder,
                                    alignItems: "center",
                                  }}
                                >
                                  <CustomCheckbox
                                    onChange={(e) => handleCheckboxChange(e, row.productReturnId, row.totalReturnAmount)}
                                    control={control}
                                    name={`select-${row.productReturnId}`}
                                    checked={checkedList.includes(row.productReturnId)}
                                    isDisabled={(disableCheckBox(row) || isAnyChecked) && !checkedList.includes(row.productReturnId)}
                                  />
                                  <Typography sx={colBodySx}>{row.productName}</Typography>
                                  <Typography sx={colBodySx}>{row.returnDetail}</Typography>
                                  <Typography sx={colBodySx}>{row.isCreditMemoRecorded ? "Yes" : "No"}</Typography>
                                  <Typography sx={colBodySx}>{row.returnDate}</Typography>
                                  <Typography sx={{ ...colBodySx, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {row.returnReason}
                                  </Typography>
                                  <Typography sx={{ ...colBodySx, fontWeight: 700, color: mTokens.accent, textAlign: "right" }}>
                                    {priceFormatter(row.totalReturnAmount)}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: "0.88rem", color: mTokens.textSecondary, mb: 2 }}>
                        No returned product available.
                      </Typography>
                    )}
                    <Box sx={{ maxWidth: { sm: 280 } }}>
                      <FieldLabel label="Return Amt" />
                      <Box sx={inputFieldSx(!!errors.returnAmt)}>
                        <CustomCurrencyField
                          label=""
                          controllerName="returnAmt"
                          control={control}
                          isDisabled
                          size="small"
                          rules={
                            submitAction === "RerturnProductSelected" && recordCreditMemo?.productReturnDTOList?.length > 0
                              ? {
                                  required: "Please select the returned product",
                                  min: { value: 1, message: "Please select the returned product" },
                                }
                              : {}
                          }
                        />
                      </Box>
                    </Box>
                  </Box>
                )}

                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={6}>
                    <FieldLabel label="Credit Amt" required />
                    <Box sx={inputFieldSx(!!errors.creditAmt)}>
                      <CustomCurrencyField
                        controllerName="creditAmt"
                        control={control}
                        label=""
                        size="small"
                        rules={
                          submitAction === "recordedCredit" || submitAction === "RerturnProductSelected"
                            ? {
                                required: "Credit Amount is required",
                                min: { value: 0.01, message: "Minimum value allowed is 0.01" },
                                max: {
                                  value: recordCreditMemo?.remainingAmount,
                                  message: " Credit amount should not be higher than Remaining Payments",
                                },
                              }
                            : {}
                        }
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <FieldLabel label="Comments" />
                    <Box sx={inputFieldSx()}>
                      <CustomTextField
                        control={control}
                        controllerName="comments"
                        label=""
                        size="small"
                        multiline
                        rows={3}
                        placeholder="Enter comments..."
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={1.2} justifyContent="flex-end" mt={1}>
                      <Button
                        size="large"
                        startIcon={<CheckCircleOutline />}
                        type="submit"
                        removeUpperCase
                        onClick={() => {
                          if (creditMemoForProduct) {
                            setSubmitAction("recordedCreditAgainstReturnProduct");
                          } else {
                            setSubmitAction("recordedCredit");
                          }
                        }}
                        sx={{
                          ...primaryBtnSx,
                          minWidth: { sm: 200 },
                          backgroundColor: mTokens.accent,
                          boxShadow: "0 4px 18px rgba(20,184,166,.35)",
                          "& .MuiTypography-root": { color: "#ffffff !important" },
                          "& .MuiButton-startIcon": { color: "#ffffff" },
                        }}
                      >
                        Record Credit Memo
                      </Button>
                      <Button
                        color="error"
                        variant="outlined"
                        removeUpperCase
                        startIcon={<Cancel />}
                        onClick={closeOrGoBack}
                        sx={{
                          ...cancelBtnSx,
                          minWidth: { sm: 140 },
                          "& .MuiTypography-root": { color: `${mTokens.error} !important` },
                          "& .MuiButton-startIcon": { color: mTokens.error },
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Box>

          {/* Right: invoice details */}
          <Box sx={{ ...cardSx, order: { xs: 1, md: 2 } }}>
            <SectionHeader icon={<ReceiptLongOutlined fontSize="small" />} title="Invoice Details" />
            <Box sx={{ px: 3.2, py: 2 }}>
              <InfoRow label="INV #" value={invNumber} />
              {selectedInvoice?.purchaseAndBill === "Y" && (
                <InfoRow label="Invoice Reference Id" value={selectedInvoice?.invReferenceId || "-"} />
              )}
              <Box display="flex" alignItems="center" py={1.15} sx={{ borderBottom: `1px solid ${mTokens.border}` }}>
                <Typography sx={{ fontSize: "0.82rem", color: mTokens.textSecondary, width: "45%", flexShrink: 0 }}>
                  Order ID
                </Typography>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  variant="body2"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleOrderDetailsNavigate(selectedInvoice);
                  }}
                  sx={{
                    fontSize: "0.88rem",
                    fontWeight: 600,
                    color: "#42A5F5",
                    p: 0,
                    m: 0,
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                  }}
                >
                  {recordCreditMemo?.orderNumber}
                </Link>
              </Box>
              <InfoRow label="INV Date" value={invDate} />
              <InfoRow label="MFR" value={mfrName} />
              <InfoRow label="Practice" value={practiceName} />
              <InfoRow label="Agency" value={practice?.agencyName ?? agencyNameFallback} />
              <Box sx={{ borderTop: `1px solid ${mTokens.border}`, mt: 1, pt: 1 }}>
                <InfoRow
                  label="INV Amt"
                  value={priceFormatter(selectedInvoice?.invoiceAmount ? selectedInvoice?.invoiceAmount : 0)}
                />
                <InfoRow
                  label="Shipping charges"
                  value={priceFormatter(selectedInvoice?.shippingCharges ? selectedInvoice.shippingCharges : 0)}
                />
                <InfoRow label="Total Amount" value={priceFormatter(totalAmount)} highlight />
                <InfoRow
                  label="Remaining Payments"
                  value={priceFormatter(recordCreditMemo?.remainingAmount ? recordCreditMemo?.remainingAmount : 0)}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </form>
    </Box>
  );
};

export default RecordCreditMemo;
